"use client";

import { motion } from "framer-motion";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import { useState } from "react";
import Button from "./Button";

export default function HomeHero() {
	const [currentSlide, setCurrentSlide] = useState(0);
	const [loaded, setLoaded] = useState(false);

	const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
		{
			initial: 0,
			loop: true,
			slideChanged(slider) {
				setCurrentSlide(slider.track.details.rel);
			},
			created() {
				setLoaded(true);
			},
		},
		[
			(slider) => {
				let timeout: ReturnType<typeof setTimeout>;
				let mouseOver = false;

				function clearNextTimeout() {
					clearTimeout(timeout);
				}

				function nextTimeout() {
					clearTimeout(timeout);
					if (mouseOver) return;
					timeout = setTimeout(() => {
						slider.next();
					}, 3000);
				}

				slider.on("created", () => {
					slider.container.addEventListener("mouseover", () => {
						mouseOver = true;
						clearNextTimeout();
					});
					slider.container.addEventListener("mouseout", () => {
						mouseOver = false;
						nextTimeout();
					});
					nextTimeout();
				});

				slider.on("dragStarted", clearNextTimeout);
				slider.on("animationEnded", nextTimeout);
				slider.on("updated", nextTimeout);
			},
		],
	);

	return (
		<section className="relative h-[calc(100vh-5rem)] w-full overflow-hidden">
			<div ref={sliderRef} className="keen-slider h-full">
				<div className="keen-slider__slide relative h-full">
					<Image
						src="/assets/black/5.jpg"
						alt="RFFA young football players training on the field"
						fill
						priority
						className="object-cover"
						quality={90}
					/>
					<div className="absolute inset-0 bg-black/50" />

					<div className="absolute top-0 left-0 w-25 h-25 md:w-35 md:h-35 lg:w-50 lg:h-50">
						<Image
							src="/assets/ball.png"
							alt=""
							fill
							className="object-cover opacity-70"
						/>
					</div>

					<div className="absolute inset-0 flex items-center justify-center">
						<div className="container mx-auto px-4 text-center">
							<motion.h1
								initial={{ opacity: 0, y: -30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, ease: "easeOut" }}
								className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 uppercase tracking-wide"
							>
								CATCHING THEM <br /> YOUNG
							</motion.h1>

							<div className="max-w-3xl mx-auto mb-8">
								<motion.p
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
									className="text-white text-base md:text-lg leading-relaxed"
								>
									Welcome Reliance Future Football Academy – where passion meets
									performance. Our mission is to train, inspire, and develop the
									next generation of football champions. With discipline,
									teamwork, and world-class coaching, we turn dreams into goals.
								</motion.p>
							</div>

							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
							>
								<Button variant="secondary" href="/registration">
									Register Now
								</Button>
							</motion.div>
						</div>
					</div>
				</div>

				<div className="keen-slider__slide relative h-full">
					<Image
						src="/assets/black/12.png"
						alt="Young player holding football in stadium"
						fill
						className="object-cover"
						quality={90}
					/>
					<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />

					<div className="absolute inset-0">
						<div className="container mx-auto px-4 h-full flex flex-col justify-between py-12">
							<motion.div
								initial={{ opacity: 0, y: -30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, ease: "easeOut" }}
								className="text-center"
							>
								<h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white uppercase tracking-wide">
									THE JOURNEY TO
									<br />
									GREATNESS <span className="text-blue-500">STARTS HERE</span>
								</h1>
							</motion.div>

							<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-end">
								<motion.div
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
									className="bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl p-6"
								>
									<h3 className="text-white text-xl md:text-2xl font-bold mb-4">
										Shaping Tomorrow&apos;s Football Stars, One Player At A Time
									</h3>

									<div className="flex items-center gap-2 mb-4">
										<div className="w-10 h-10 rounded-full bg-white border-2 border-white overflow-hidden relative">
											<Image
												src="/assets/black/guy1.png"
												alt="Player 1"
												fill
												className="object-cover"
											/>
										</div>
										<div className="w-10 h-10 rounded-full bg-white border-2 border-white overflow-hidden -ml-3 relative">
											<Image
												src="/assets/black/guy2.png"
												alt="Player 2"
												fill
												className="object-cover"
											/>
										</div>
										<div className="w-10 h-10 rounded-full bg-white border-2 border-white overflow-hidden -ml-3 relative">
											<Image
												src="/assets/black/guy3.png"
												alt="Player 3"
												fill
												className="object-cover"
											/>
										</div>
										<div className="w-10 h-10 rounded-full bg-white border-2 border-white flex items-center justify-center -ml-3">
											<span className="text-blue-600 font-bold text-sm">→</span>
										</div>
									</div>
								</motion.div>

								<div />

								<motion.div
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
									className="bg-blue-600 rounded-2xl p-6"
								>
									<h3 className="text-white text-xl md:text-2xl font-bold mb-4">
										Experience The Thrill of Football With RFFA
									</h3>
									<Button
										variant="secondary"
										href="/programs"
										title="Explore the programs we offer"
									>
										Explore
									</Button>
								</motion.div>
							</div>
						</div>
					</div>
				</div>

				<div className="keen-slider__slide relative h-full">
					<Image
						src="/assets/black/13.png"
						alt="Football player training with ball"
						fill
						className="object-cover"
						quality={90}
					/>
					<div className="absolute inset-0 bg-black/60" />

					<div className="absolute inset-0 flex items-center justify-center">
						<div className="container mx-auto px-4 text-center">
							<motion.h1
								initial={{ opacity: 0, y: -30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, ease: "easeOut" }}
								className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 uppercase tracking-wide"
							>
								TRAINING AND SKILLS
							</motion.h1>

							<div className="max-w-3xl mx-auto mb-8">
								<motion.p
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
									className="text-white text-base md:text-lg leading-relaxed"
								>
									Welcome Reliance Future Football Academy – where passion meets
									performance. Our mission is to train, inspire, and develop the
									next generation of football champions. With discipline,
									teamwork, and world-class coaching, we turn dreams into goals.
								</motion.p>
							</div>

							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
							>
								<Button variant="secondary" href="/registration">
									Register Now
								</Button>
							</motion.div>
						</div>
					</div>
				</div>

				<div className="keen-slider__slide relative h-full">
					<Image
						src="/assets/black/14.png"
						alt="Young football players celebrating with trophy"
						fill
						className="object-cover"
						quality={90}
					/>
					<div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />

					<div className="absolute inset-0 flex items-center">
						<div className="container mx-auto px-4">
							<div className="max-w-2xl">
								<motion.h1
									initial={{ opacity: 0, x: -30 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.8, ease: "easeOut" }}
									className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-8 uppercase tracking-wide leading-tight"
								>
									BUILDING FUTURE
									<br />
									FOOTBALL STARS
								</motion.h1>

								<motion.div
									initial={{ opacity: 0, x: -30 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
								>
									<Button variant="secondary" href="/registration">
										Join The Team
									</Button>
								</motion.div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{loaded && instanceRef.current && (
				<div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-10">
					{[0, 1, 2, 3].map((idx) => (
						<button
							key={idx}
							onClick={() => {
								instanceRef.current?.moveToIdx(idx);
							}}
							className={`transition-all duration-300 rounded-full ${
								currentSlide === idx
									? "w-12 h-3 bg-white"
									: "w-3 h-3 bg-white/50 hover:bg-white/75"
							}`}
							aria-label={`Go to slide ${idx + 1}`}
						/>
					))}
				</div>
			)}

			{loaded && instanceRef.current && (
				<>
					<button
						onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
							e.stopPropagation();
							instanceRef.current?.prev();
						}}
						className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 group hidden md:block"
						aria-label="Previous slide"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2.5}
							stroke="currentColor"
							className="w-6 h-6 group-hover:scale-110 transition-transform"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M15.75 19.5L8.25 12l7.5-7.5"
							/>
						</svg>
					</button>

					<button
						onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
							e.stopPropagation();
							instanceRef.current?.next();
						}}
						className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 group hidden md:block"
						aria-label="Next slide"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2.5}
							stroke="currentColor"
							className="w-6 h-6 group-hover:scale-110 transition-transform"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M8.25 4.5l7.5 7.5-7.5 7.5"
							/>
						</svg>
					</button>
				</>
			)}
		</section>
	);
}
